// src/utils/membershipEndAdmin.js
// Payment Settings "End a membership" (admin only; FLIP_GAPS_DESIGN.md
// section 2; flip-day checklist B6 and C6 step 7).
//
// Form checks, payload shaping and every string for the one admin control
// that calls adminEndMembership (functions/modules/memberships.js). The
// server decides everything and checks the caller again (assertAdmin); this
// file only keeps the form from sending something the server would refuse
// for a reason the admin can fix on the spot, and words the confirm and the
// result.
//
// Kind (evergreen 3.5b, STEP3_5B_MERGE_DELTA.md 4.8 and section 6 A-4): a
// channel membership names its creator uid (the payload's creatorUid, as
// before 3.5b, byte for byte); a team membership names its team id (the
// payload's {kind: 'team', targetId}). The server's adminEndMembership takes
// both.
//
// The server's rules this mirrors (change both together):
//  - isMembershipUid: 1-128 characters, no '_' or '/', not '.' or '..';
//  - a team id: isMembershipUid plus TEAM_ID_RE /^[A-Za-z0-9]{10,40}$/
//    (memberships.js teamIdArg);
//  - SUB_ID_RE: /^sub_[A-Za-z0-9]{8,64}$/ (a subscription id not in the pair
//    is refused on the server: "That subscription does not belong to this
//    membership.");
//  - ground 'operational' | 'member_request' | 'violation', when 'now' |
//    'period_end', refundLatest a boolean (false for period_end), the note
//    trimmed and kept to 1000 characters;
//  - an end now that is not for a rules violation needs refundLatest, or
//    waiveRefund with a note (the pro-rata refusal, word for word below).
//
// Errors show the server's sentence with its token stripped (the same rule
// as membershipPayment.js membershipErrorMessage, copied so this file keeps
// zero imports), except membership-not-found, whose server sentence is
// worded for payers.
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-membership-end-admin.mjs). ASCII only.

export const END_COPY = Object.freeze({
  title: 'End a membership',
  intro: 'Ends one channel or team membership on the server, with the refund and the ending notice. '
    + 'Use it for the dark check and for support. Never end or cancel a membership in the Stripe dashboard.',
  // Labels.
  kind: 'Kind',
  creatorUid: 'Creator uid',
  teamId: 'Team id',
  viewerUid: 'Member uid',
  useMine: 'Use my account',
  subscriptionId: 'Subscription id (optional)',
  reason: 'Reason',
  when: 'When',
  refund: 'Refund the latest payment to the card',
  waive: 'End now without a refund',
  note: 'Note',
  submit: 'End membership',
  submitting: 'Ending...',
  // Hints.
  subscriptionHint: "Leave it blank to end the membership's current subscription.",
  refundOffHint: 'Nothing is refunded when it ends at the end of the paid period.',
  waiveHint: 'Only with a note.',
  // Name lines (public_profiles/{uid}.userName).
  noProfile: 'No public profile for this uid.',
  // Form checks. noteRequired is DisputesAdmin's sentence; proRata is the
  // server's own refusal (memberships.js, token pro-rata).
  badUids: 'Enter a valid creator uid and member uid.',
  badTeamUids: 'Enter a valid team id and member uid.',
  sameUids: 'The creator and the member must be different accounts.',
  badSubscription: 'That is not a subscription id.',
  noteRequired: 'A note is required.',
  proRata: 'End at period end or refund the latest payment.',
  // Confirm.
  confirmTitle: 'End this membership?',
  confirmFoot: 'The member gets the ending notice. This cannot be undone.',
  // Errors.
  notFound: 'No membership found for this creator and member.',
  notFoundTeam: 'No membership found for this team and member.',
  fallbackError: 'Something went wrong. Please try again.',
});

// Kind (3.5b): Channel first, the default.
export const KINDS = Object.freeze(['channel', 'team']);
export const KIND_LABELS = Object.freeze({
  channel: 'Channel',
  team: 'Team',
});

export const GROUNDS = Object.freeze(['operational', 'member_request', 'violation']);
export const GROUND_LABELS = Object.freeze({
  operational: 'Operational',
  member_request: 'Member asked',
  violation: 'Rules violation',
});
export const WHENS = Object.freeze(['now', 'period_end']);
export const WHEN_LABELS = Object.freeze({
  now: 'Now',
  period_end: 'At the end of the paid period',
});

export const NOTE_MAX = 1000;
export const SUB_ID_RE = /^sub_[A-Za-z0-9]{8,64}$/;
// memberships.js TEAM_ID_RE (the team id shape teamIdArg checks).
export const TEAM_ID_RE = /^[A-Za-z0-9]{10,40}$/;

// The form's starting values: Channel, Operational, Now, refund on, no waiver.
export const DEFAULT_FORM = Object.freeze({
  kind: 'channel',
  creatorUid: '',
  teamId: '',
  viewerUid: '',
  subscriptionId: '',
  ground: 'operational',
  when: 'now',
  refund: true,
  waive: false,
  note: '',
});

/** A uid the server accepts (memberships.js isMembershipUid). */
export function isMembershipUid(s) {
  return typeof s === 'string' && s.length >= 1 && s.length <= 128
    && !s.includes('_') && !s.includes('/') && s !== '.' && s !== '..';
}

/** A team id the server accepts (memberships.js teamIdArg). */
export function isTeamId(s) {
  return isMembershipUid(s) && TEAM_ID_RE.test(s);
}

const str = (v) => (typeof v === 'string' ? v.trim() : '');
const kindOf = (form) => (KINDS.includes(form?.kind) ? form.kind : DEFAULT_FORM.kind);
const noteOf = (form) => str(form?.note).slice(0, NOTE_MAX);
const groundOf = (form) => (GROUNDS.includes(form?.ground) ? form.ground : DEFAULT_FORM.ground);
const whenOf = (form) => (WHENS.includes(form?.when) ? form.when : DEFAULT_FORM.when);

/**
 * What the switches mean right now: the refund only for an end now (forced
 * off for period end, as the server requires); "End now without a refund"
 * only for an end now with the refund off and a reason other than a rules
 * violation (a violation ends now without a refund and without a waiver).
 */
export function effectiveRefund(form) {
  const now = whenOf(form) === 'now';
  const refundLatest = now && form?.refund === true;
  const waiveShown = now && !refundLatest && groundOf(form) !== 'violation';
  const waiveRefund = waiveShown && form?.waive === true;
  return { refundLatest, waiveShown, waiveRefund };
}

/**
 * The first problem the admin can fix in the form, or null. A team (3.5b)
 * checks its team id and the member uid instead of the two uids (a team id
 * is never an account, so there is no same-account check).
 */
export function checkForm(form) {
  const viewerUid = str(form?.viewerUid);
  if (kindOf(form) === 'team') {
    if (!isTeamId(str(form?.teamId)) || !isMembershipUid(viewerUid)) return END_COPY.badTeamUids;
  } else {
    const creatorUid = str(form?.creatorUid);
    if (!isMembershipUid(creatorUid) || !isMembershipUid(viewerUid)) return END_COPY.badUids;
    if (creatorUid === viewerUid) return END_COPY.sameUids;
  }
  const sub = str(form?.subscriptionId);
  if (sub && !SUB_ID_RE.test(sub)) return END_COPY.badSubscription;
  const note = noteOf(form);
  if (!note) return END_COPY.noteRequired;
  const { refundLatest, waiveRefund } = effectiveRefund(form);
  if (whenOf(form) === 'now' && groundOf(form) !== 'violation' && !refundLatest && !waiveRefund) return END_COPY.proRata;
  return null;
}

/**
 * The adminEndMembership payload: {creatorUid, viewerUid, ground, when,
 * refundLatest, note} for a channel (as before 3.5b), or {kind: 'team',
 * targetId: teamId, viewerUid, ground, when, refundLatest, note} for a team;
 * plus subscriptionId only when one was entered, and waiveRefund: true only
 * for an end now with the refund off, a reason other than a rules violation,
 * the waiver switched on and a note.
 */
export function endPayload(form) {
  const { refundLatest, waiveRefund } = effectiveRefund(form);
  const note = noteOf(form);
  const sub = str(form?.subscriptionId);
  const target = kindOf(form) === 'team'
    ? { kind: 'team', targetId: str(form?.teamId) }
    : { creatorUid: str(form?.creatorUid) };
  const out = {
    ...target,
    viewerUid: str(form?.viewerUid),
    ground: groundOf(form),
    when: whenOf(form),
    refundLatest,
    note,
  };
  if (sub) out.subscriptionId = sub;
  if (waiveRefund && note) out.waiveRefund = true;
  return out;
}

/** "Creator: {name}" / "Member: {name}" under each uid field. */
export function nameLine(role, name) {
  return role === 'creator' ? `Creator: ${name}` : `Member: ${name}`;
}

const who = (name, uid) => (typeof name === 'string' && name.trim() ? `${name.trim()} (${uid})` : uid);

/**
 * The confirm, one line each, from the payload it will send. A team payload
 * (3.5b) names its team by id ("Team id: {id}", the form's own label): the
 * form reads no team doc.
 */
export function confirmText(payload, names = {}) {
  const p = payload || {};
  const refund = p.refundLatest ? 'the latest payment, to the card'
    : (p.waiveRefund ? 'none, waived with a note' : 'none');
  return [
    END_COPY.confirmTitle,
    p.kind === 'team' ? `${END_COPY.teamId}: ${p.targetId}` : `Creator: ${who(names.creator, p.creatorUid)}`,
    `Member: ${who(names.viewer, p.viewerUid)}`,
    `Subscription: ${p.subscriptionId || 'the current one'}`,
    `When: ${p.when === 'now' ? 'now' : 'at the end of the paid period'}`,
    `Refund: ${refund}`,
    `Reason: ${GROUND_LABELS[p.ground] || p.ground}`,
    `Note: ${p.note}`,
    END_COPY.confirmFoot,
  ].join('\n');
}

/** {error} for a form problem, else {payload, confirmText}. */
export function planEnd(form, names = {}) {
  const error = checkForm(form);
  if (error) return { error };
  const payload = endPayload(form);
  return { payload, confirmText: confirmText(payload, names) };
}

/** ms -> "October 26, 2026" (America/Chicago); '' for a missing date. */
export function formatEndDate(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  const opts = { month: 'long', day: 'numeric', year: 'numeric' };
  try {
    return new Date(n).toLocaleDateString('en-US', { ...opts, timeZone: 'America/Chicago' });
  } catch (_) {
    return new Date(n).toLocaleDateString('en-US', opts);
  }
}

function refundWord(refund) {
  const r = refund && typeof refund === 'object' ? refund : {};
  if (r.state === 'refunded') return typeof r.refundId === 'string' && r.refundId ? `refunded (${r.refundId})` : 'refunded';
  if (r.state === 'already_refunded') return 'already refunded';
  if (r.state === 'waived') return 'waived';
  return 'none';
}

/**
 * The line after the server replies ({ended, endsAtMs, refund: {state,
 * refundId}}), for the "when" that was sent:
 *  - an end now that ended: "Ended now. Refund: {...}."
 *  - a period end that is set: "Set to end on {date}. No refund."
 *  - a period end that ended at once (an unpaid period, or one already
 *    over): "Ended on {date}. No refund."
 *  - anything that did not end and has no end date (Stripe refused the
 *    cancel or no longer has the subscription; the server logged it):
 *    "Not ended yet. Refund: {...}. Try again in a moment." A retry never
 *    refunds twice (the server keeps the refund's state).
 */
export function resultLine(reply, when) {
  const r = reply && typeof reply === 'object' ? reply : {};
  const date = formatEndDate(r.endsAtMs);
  if (when === 'now') {
    return r.ended === true
      ? `Ended now. Refund: ${refundWord(r.refund)}.`
      : `Not ended yet. Refund: ${refundWord(r.refund)}. Try again in a moment.`;
  }
  if (r.ended === true) return date ? `Ended on ${date}. No refund.` : 'Ended now. Refund: none.';
  if (date) return `Set to end on ${date}. No refund.`;
  return 'Not ended yet. Refund: none. Try again in a moment.';
}

/**
 * The server's sentence (token stripped), or this control's own not-found
 * line for the kind that was sent ('team' for a team end; anything else is
 * a channel).
 */
export function endErrorText(err, kind = 'channel') {
  const raw = String(err?.message || '').trim();
  if (raw.includes('membership-not-found')) return kind === 'team' ? END_COPY.notFoundTeam : END_COPY.notFound;
  const stripped = raw.replace(/^[a-z][a-z-]*:\s*/, '').trim();
  return /\s/.test(stripped) ? stripped : END_COPY.fallbackError;
}
